/**
 * "Report a problem / Request a change" — the dialog (Card P244).
 *
 * Target: under thirty seconds. Two choices, one required sentence, Submit.
 * Everything else about the report is captured for them.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { snapshot } from '../lib/diagnostics';
import { APP_VERSION, APP_COMMIT } from '../lib/appVersion';

const TYPES = [
  { key: 'problem', label: 'Problem', hint: 'Something is wrong' },
  { key: 'change', label: 'Change', hint: 'This should work differently' },
  { key: 'idea', label: 'New idea', hint: 'Something we do not have' },
  { key: 'question', label: 'Question', hint: 'I need to understand something' },
];

const PRIORITIES = [
  { key: 'urgent', label: 'Urgent', hint: 'Blocks my work' },
  { key: 'soon', label: 'Soon', hint: 'Slows me down' },
  { key: 'when_possible', label: 'When possible', hint: 'No rush' },
];

/*
 * The lifecycle labels (Card P245, migration 208). This is the SECOND copy of
 * the vocabulary in this file's package - the triage page has the other - and
 * the parity control pins BOTH key sets to routes/_feedback_shared.js.
 * It had to: the first version of that control looked for QUOTED literals and
 * went green over this very map, whose keys are unquoted.
 */
const STATUS_LABEL = {
  reported: 'Reported', triaged: 'Triaged', diagnosed: 'Diagnosed',
  fix_pr: 'Fix PR open', merged: 'Merged', released: 'Released',
  verified: 'Verified', closed: 'Closed', reopened: 'Reopened',
  wont_do: "Won't do",
};

/** The capture is downscaled before it is ever encoded. */
const MAX_CAPTURE_WIDTH = 1600;

/*
 * ⛔⛔ THE ONE STATUS THAT MEANS "DONE" TO A REPORTER — Card P245.
 *
 * Migration 208 renamed this state from 'live' to 'released'. THE CLIENT ACTS
 * FIRST: it decides whether the banner appears, and it writes the ack back. So
 * a stale literal here does not fail — it silently matches nothing, and every
 * reporter stops being told their request shipped, with no error anywhere.
 *
 * src/ cannot import routes/ (src is not copied into the runtime image), so
 * this cannot be a shared import. tests/unit/security/feedbackStatusParity
 * .test.js reads this file and routes/_feedback_shared.js and fails if the two
 * disagree — the same shape as the redaction parity control on this feature.
 * ⛔ Do not inline this value at the use sites; the control matches it here.
 */
const REPORTER_DONE_STATUS = 'released';

function apiOf() {
  return (typeof window !== 'undefined' && window.zignagenixAPI) || null;
}

export default function FeedbackDialog({ onClose, pageRoute, pageContext }) {
  const [tab, setTab] = useState('new');
  const [requestType, setRequestType] = useState('problem');
  const [priority, setPriority] = useState('soon');
  const [summary, setSummary] = useState('');
  const [expected, setExpected] = useState('');
  const [shot, setShot] = useState(null);          // data URL, after edits
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);           // { reference, screenshotStored }
  const [error, setError] = useState(null);
  const [mine, setMine] = useState(null);
  const [hidden, setHidden] = useState(false);      // dialog hides itself during capture

  const dialogRef = useRef(null);

  useEffect(() => {
    if (tab !== 'mine' || mine) return;
    const api = apiOf();
    if (!api) return;
    let alive = true;
    api.invoke('feedback:mine').then((res) => {
      if (!alive) return;
      setMine(res && res.requests ? res.requests : []);
    }).catch(() => { if (alive) setMine([]); });
    return () => { alive = false; };
  }, [tab, mine]);

  /*
   * ⛔⛔ THE "YOUR REQUEST IS DONE" NOTICE, AND WHY IT NEEDS THE ACK CHANNEL.
   *
   * Card item 4: a reporter should see when their own request goes Live. The
   * notice has to appear ONCE — a banner that returns every time they open the
   * dialog stops being news and starts being furniture, and they will read
   * past the next one.
   *
   * So "has this person seen this status" is SERVER state
   * (feedback_requests.reporter_ack_status), not a localStorage flag: the same
   * member of staff uses the CRM from the clinic desktop and from an iPad, and
   * a per-browser flag would show the notice again on the other device and
   * never on the first one.
   *
   * ⚠ This wiring was MISSING from my first draft — the handler and the column
   * existed and nothing ever called them. tests/unit/security/
   * uncalledChannelRatchet.test.js caught it: a channel with no caller is a
   * paragraph, not a feature.
   */
  const done = (mine || []).filter((r) => (
    r.status === REPORTER_DONE_STATUS && r.reporter_ack_status !== REPORTER_DONE_STATUS
  ));

  const dismissDone = useCallback(async () => {
    const api = apiOf();
    if (!api) return;
    const refs = done.map((r) => r.reference);
    setMine((rows) => (rows || []).map((r) => (
      refs.includes(r.reference) ? { ...r, reporter_ack_status: REPORTER_DONE_STATUS } : r
    )));
    await Promise.all(refs.map((reference) => (
      api.invoke('feedback:ack', { reference }).catch(() => {})
    )));
  }, [done]);

  /*
   * ⛔⛔ PIXELS ARE ONLY EVER CREATED ON REQUEST.
   *
   * The obvious implementation captures the screen when the launcher is
   * clicked — before the dialog covers it — and offers the image if the user
   * wants it. That is easier and it is wrong: it renders a clinician's screen
   * into memory every time anybody opens this dialog, including the times they
   * came to ask a question and never wanted a picture at all.
   *
   * So capture happens on the button, and the dialog HIDES ITSELF for one
   * frame so it is not in its own screenshot. The cost is a brief flicker.
   */
  const capture = useCallback(async () => {
    setCaptureError(null);
    setCapturing(true);
    setHidden(true);
    try {
      await new Promise((r) => window.requestAnimationFrame(() => window.requestAnimationFrame(r)));
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(window.document.body, {
        logging: false,
        useCORS: true,
        scale: Math.min(1, MAX_CAPTURE_WIDTH / Math.max(1, window.document.body.scrollWidth)),
      });
      setShot(canvas.toDataURL('image/png'));
    } catch (e) {
      setCaptureError('The picture could not be taken. You can still send the report without it.');
    } finally {
      setHidden(false);
      setCapturing(false);
    }
  }, []);

  const submit = useCallback(async () => {
    const api = apiOf();
    if (!api) { setError('You appear to be offline. Please try again in a moment.'); return; }
    setBusy(true);
    setError(null);
    try {
      const diag = snapshot();
      const res = await api.invoke('feedback:submit', {
        requestType,
        priority,
        summary,
        expected: expected || null,
        screenshotBase64: shot || null,
        context: {
          route: pageRoute || '',
          pageContext: pageContext || {},
          appVersion: APP_VERSION,
          appCommit: APP_COMMIT,
          browser: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          screenSize: typeof window !== 'undefined'
            ? `${window.innerWidth}x${window.innerHeight}` : '',
          breadcrumbs: diag.breadcrumbs,
          apiCalls: diag.apiCalls,
          errors: diag.errors,
        },
      });
      if (res && res.error) {
        setError(res.message || 'That could not be sent. Please try again.');
      } else {
        setSent(res);
      }
    } catch (e) {
      setError('That could not be sent — the connection failed. Your text is still here, so you can try again.');
    } finally {
      setBusy(false);
    }
  }, [requestType, priority, summary, expected, shot, pageRoute, pageContext]);

  if (hidden) return null;

  return (
    <div className="fb-backdrop" role="presentation">
      <div
        className="fb-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fb-title"
        ref={dialogRef}
      >
        <div className="fb-head">
          <h2 id="fb-title">{sent ? 'Thank you' : 'Report a problem or request a change'}</h2>
          <button type="button" className="fb-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {sent ? (
          <div className="fb-sent">
            <p className="fb-sent-ref">
              Request <strong>{sent.reference}</strong> sent to Daniel.
            </p>
            <p className="fb-sent-note">
              You can see what happens to it under <em>My requests</em> in this
              same window. {sent.screenshotStored
                ? 'Your picture was attached.'
                : (shot ? 'Your picture could not be attached, but the report was sent.' : '')}
            </p>
            <div className="fb-actions">
              <button type="button" className="fb-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            <div className="fb-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'new'}
                className={tab === 'new' ? 'fb-tab fb-tab--on' : 'fb-tab'}
                onClick={() => setTab('new')}>New request</button>
              <button type="button" role="tab" aria-selected={tab === 'mine'}
                className={tab === 'mine' ? 'fb-tab fb-tab--on' : 'fb-tab'}
                onClick={() => setTab('mine')}>My requests</button>
            </div>

            {tab === 'mine' ? (
              <div className="fb-body">
                {done.length > 0 && (
                  <div className="fb-done" role="status">
                    <span>
                      {done.length === 1
                        ? `Your request ${done[0].reference} is done — it is live now.`
                        : `${done.length} of your requests are done — they are live now.`}
                    </span>
                    <button type="button" className="fb-tool" onClick={dismissDone}>
                      Got it
                    </button>
                  </div>
                )}
                {mine === null && <p className="fb-muted">Loading…</p>}
                {mine && mine.length === 0 && <p className="fb-muted">You have not sent any requests yet.</p>}
                {mine && mine.length > 0 && (
                  <ul className="fb-mine">
                    {mine.map((r) => (
                      <li key={r.id} className="fb-mine-row">
                        <span className="fb-ref">{r.reference}</span>
                        <span className={`fb-pill fb-pill--${r.status}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        <span className="fb-mine-summary">{r.summary}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="fb-body">
                <fieldset className="fb-choices">
                  <legend>What kind of request is this?</legend>
                  <div className="fb-chip-row">
                    {TYPES.map((t) => (
                      <button key={t.key} type="button"
                        className={requestType === t.key ? 'fb-chip fb-chip--on' : 'fb-chip'}
                        aria-pressed={requestType === t.key}
                        onClick={() => setRequestType(t.key)}>
                        <span className="fb-chip-label">{t.label}</span>
                        <span className="fb-chip-hint">{t.hint}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="fb-choices">
                  <legend>How urgent is it?</legend>
                  <div className="fb-chip-row">
                    {PRIORITIES.map((p) => (
                      <button key={p.key} type="button"
                        className={priority === p.key ? 'fb-chip fb-chip--on' : 'fb-chip'}
                        aria-pressed={priority === p.key}
                        onClick={() => setPriority(p.key)}>
                        <span className="fb-chip-label">{p.label}</span>
                        <span className="fb-chip-hint">{p.hint}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="fb-label" htmlFor="fb-summary">
                  What happened, or what do you need? <span className="fb-req">required</span>
                </label>
                <textarea id="fb-summary" className="fb-text" rows={4}
                  value={summary} onChange={(e) => setSummary(e.target.value)}
                  placeholder="In your own words — one or two sentences is plenty." />

                <label className="fb-label" htmlFor="fb-expected">
                  What did you expect? <span className="fb-opt">optional</span>
                </label>
                <textarea id="fb-expected" className="fb-text" rows={2}
                  value={expected} onChange={(e) => setExpected(e.target.value)} />

                <div className="fb-shot">
                  {!shot ? (
                    <button type="button" className="fb-secondary" onClick={capture} disabled={capturing}>
                      {capturing ? 'Taking the picture…' : 'Attach a picture of my screen'}
                    </button>
                  ) : (
                    <ScreenshotEditor
                      src={shot}
                      onChange={setShot}
                      onRemove={() => setShot(null)}
                    />
                  )}
                  {captureError && <p className="fb-warn">{captureError}</p>}
                </div>

                {/*
                  * ⛔ PERMANENT, NOT DISMISSIBLE, AND NOT CONDITIONAL ON HAVING
                  * ATTACHED A PICTURE. The free-text boxes are the likeliest
                  * route for a patient detail to arrive, so the notice has to
                  * be visible while the user is typing into them.
                  */}
                <p className="fb-phi-note">
                  Please don’t include patient details you don’t need to. Everything about
                  the page, your recent clicks and any errors is already captured for you.
                </p>

                {error && <p className="fb-error" role="alert">{error}</p>}

                <div className="fb-actions">
                  <button type="button" className="fb-ghost" onClick={onClose}>Cancel</button>
                  <button type="button" className="fb-primary"
                    onClick={submit} disabled={busy || !summary.trim()}>
                    {busy ? 'Sending…' : 'Send to Daniel'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Preview, crop and hide-an-area.
 *
 * ⚠⚠ "BLUR" IS IMPLEMENTED AS A SOLID BLOCK, DELIBERATELY. The card asked for
 * crop/blur. A blur is a reversible-ish transform — it preserves the
 * underlying structure, and text blurred at a modest radius has been recovered
 * from screenshots before. A filled rectangle destroys the pixels. For a
 * control whose job is "remove the patient's name from this picture", the
 * version that cannot be undone is the right one, and it is also easier to see
 * that it worked.
 */
export function ScreenshotEditor({ src, onChange, onRemove }) {
  const canvasRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [mode, setMode] = useState('hide');

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new window.Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
    };
    img.src = src;
  }, [src]);

  useEffect(() => { draw(); }, [draw]);

  const toCanvas = (ev) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((ev.clientX - rect.left) / rect.width) * canvas.width,
      y: ((ev.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const finish = (end) => {
    const canvas = canvasRef.current;
    if (!canvas || !drag) return;
    const x = Math.min(drag.x, end.x), y = Math.min(drag.y, end.y);
    const w = Math.abs(end.x - drag.x), h = Math.abs(end.y - drag.y);
    setDrag(null);
    if (w < 4 || h < 4) return;
    const ctx = canvas.getContext('2d');
    if (mode === 'hide') {
      ctx.fillStyle = '#111827';
      ctx.fillRect(x, y, w, h);
      onChange(canvas.toDataURL('image/png'));
    } else {
      const cropped = window.document.createElement('canvas');
      cropped.width = Math.round(w); cropped.height = Math.round(h);
      cropped.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, w, h);
      onChange(cropped.toDataURL('image/png'));
    }
  };

  return (
    <div className="fb-editor">
      <div className="fb-editor-tools">
        <button type="button" aria-pressed={mode === 'hide'}
          className={mode === 'hide' ? 'fb-tool fb-tool--on' : 'fb-tool'}
          onClick={() => setMode('hide')}>Hide an area</button>
        <button type="button" aria-pressed={mode === 'crop'}
          className={mode === 'crop' ? 'fb-tool fb-tool--on' : 'fb-tool'}
          onClick={() => setMode('crop')}>Crop</button>
        <button type="button" className="fb-tool" onClick={onRemove}>Remove picture</button>
      </div>
      <p className="fb-muted fb-editor-hint">
        Drag on the picture to {mode === 'hide' ? 'cover anything that should not be sent' : 'keep only that part'}.
      </p>
      <canvas
        ref={canvasRef}
        className="fb-canvas"
        onPointerDown={(e) => setDrag(toCanvas(e))}
        onPointerUp={(e) => finish(toCanvas(e))}
      />
    </div>
  );
}
